import { createNumberFormat } from "src/lib/format";
const INSIGHT_TYPE_META = {
  DASHBOARD_INSIGHT: {
    label: "Recommendation",
    description: "Suggested action from the latest audit",
    tone: "default",
  },
  PERFORMANCE_DIAGNOSIS: {
    label: "Performance diagnosis",
    description: "Decision-tree read on the main performance constraint",
    tone: "scenario",
  },
};

export const DASHBOARD_INSIGHT_ACTION_KIND = "insight_action_plan";
const DASHBOARD_INSIGHT_THREAD_TITLE_MAX = 72;
const DASHBOARD_INSIGHT_PROMPT_MAX_LENGTH = 3800;
const DASHBOARD_INSIGHT_PROMPT_FACT_LINE_LIMIT = 6;
const DASHBOARD_INSIGHT_PROMPT_SECTION_LINE_LIMIT = 4;
const DASHBOARD_INSIGHT_DISPLAY_PAYLOAD_MAX_LENGTH = 11000;
const DASHBOARD_INSIGHT_DISPLAY_FACT_LIMIT = 6;
const DEFAULT_DASHBOARD_INSIGHT_PRIORITY = 50;

const metricTarget = ({ routeName, routeParams, label }) => ({
  routeName,
  routeParams,
  label,
});

const titleCaseWord = (word) =>
  word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;

const metricRouteTargets = new Map([
  [
    "aov_bucket",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "aovAnalytics" },
      label: "View AOV",
    }),
  ],
  [
    "product_cart",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "aovAnalytics" },
      label: "View AOV",
    }),
  ],
  [
    "churn_pct",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "repeat_purchase_pct",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "mer",
    metricTarget({
      routeName: "marketing",
      routeParams: { child: "marketingActivity" },
      label: "View marketing",
    }),
  ],
  [
    "ltc_cac_30",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "ltc_cac_90",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "ltc_cac_180",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "acquisition_efficiency_trend",
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "cohortAnalytics" },
      label: "View cohorts",
    }),
  ],
  [
    "marketing_spend",
    metricTarget({
      routeName: "marketing",
      routeParams: { child: "marketingActivity" },
      label: "View marketing",
    }),
  ],
  [
    "ad_spend",
    metricTarget({
      routeName: "marketing",
      routeParams: { child: "marketingActivity" },
      label: "View marketing",
    }),
  ],
  [
    "cost_of_delivery_pct",
    metricTarget({
      routeName: "finance",
      routeParams: { child: "normalizedPL" },
      label: "View P&L",
    }),
  ],
  [
    "opex_revenue_pct",
    metricTarget({
      routeName: "finance",
      routeParams: { child: "normalizedPL" },
      label: "View P&L",
    }),
  ],
  [
    "cash_conversion_cycle_days",
    metricTarget({
      routeName: "finance",
      routeParams: { child: "normalizedPL" },
      label: "View P&L",
    }),
  ],
  [
    "inventory_revenue_pct",
    metricTarget({
      routeName: "finance",
      routeParams: { child: "normalizedPL" },
      label: "View P&L",
    }),
  ],
]);

[
  "aov",
  "upt",
  "order_count_30",
  "order_count_90",
  "gross_margin_pct",
  "discount_rate_pct",
  "cac",
  "total_sales_30",
  "total_sales_90",
].forEach((metricKey) => {
  metricRouteTargets.set(
    metricKey,
    metricTarget({
      routeName: "analytics",
      routeParams: { child: "unitEconomics" },
      label: "View metric",
    })
  );
});

const acronymLabels = new Map([
  ["aov", "AOV"],
  ["upt", "UPT"],
  ["mer", "MER"],
  ["cac", "CAC"],
  ["ltcm:cac", "LTCM:CAC"],
  ["ltcm", "LTCM"],
  ["sku", "SKU"],
  ["dtc", "DTC"],
  ["p&l", "P&L"],
]);

const metricLabelOverrides = {
  mer: "MER",
  performance_diagnosis: "Performance diagnosis",
  churn_pct: "90-day acquisition cohort churn",
  repeat_purchase_pct: "90-day repeat purchase rate",
  gross_margin_pct: "Gross Profit Margin",
  ltc_cac_30: "30-day acquisition cohort LTCM:CAC proxy",
  ltc_cac_90: "90-day acquisition cohort LTCM:CAC proxy",
  ltc_cac_180: "180-day acquisition cohort LTCM:CAC proxy",
  acquisition_efficiency_trend: "nCAC change vs best comparable month",
};

const glossaryDetail = (description, formula = "") => ({
  description,
  formula,
});

const metricGlossaryDefinitions = new Map([
  [
    "repeat_purchase_pct",
    glossaryDetail(
      "Share of acquired customers who purchased again within 90 days of their first purchase. The acquisition cohort ends at least 90 days before the audit date so every customer has a full observation window. Higher is better",
      "customers with a repeat purchase within 90 days / customers acquired in the cohort"
    ),
  ],
  [
    "ltcm",
    "LTCM means Lifetime Contribution Margin: contribution margin generated by a customer within a selected 30, 90, or 180 day window",
  ],
  [
    "cac",
    "CAC means Customer Acquisition Cost: marketing spend divided by new customers acquired in the same period",
  ],
  [
    "ncac",
    glossaryDetail(
      "nCAC means new customer acquisition cost: marketing spend divided by newly acquired customers",
      "marketing spend / new orders"
    ),
  ],
  [
    "ltcm:cac",
    "LTCM:CAC compares Lifetime Contribution Margin with Customer Acquisition Cost. 1.0x is breakeven",
  ],
  [
    "aov",
    glossaryDetail(
      "AOV means Average Order Value: net revenue divided by orders. Net revenue subtracts discounts and refunds, and includes shipping collected",
      "(gross sales - discounts - refunds + shipping collected) / orders"
    ),
  ],
  ["upt", "UPT means Units per Transaction: units sold divided by orders"],
  [
    "mer",
    glossaryDetail(
      "MER means Marketing Efficiency Ratio: net revenue generated for each unit of currency spent on direct marketing",
      "net revenue / direct marketing spend"
    ),
  ],
  [
    "amer",
    glossaryDetail(
      "aMER means acquisition Marketing Efficiency Ratio: new-customer revenue divided by new customer acquisition cost",
      "new AOV / nCAC"
    ),
  ],
  [
    "ltr",
    "LTR means Lifetime Revenue: revenue generated by a customer within the measured cohort window",
  ],
  [
    "dr%",
    "DR% means Discount Rate: total discounts divided by gross revenue before discounts",
  ],
  [
    "gm%",
    "Gross Profit Margin is gross profit per order divided by average order value. Gross profit subtracts COGS, shipping paid, and transaction fees",
  ],
  [
    "aur",
    "AUR means Average Unit Retail: net revenue divided by total units sold",
  ],
  [
    "ccc",
    "CCC means Cash Conversion Cycle: inventory days plus receivable days minus payable days",
  ],
  [
    "churn",
    "Churn is the share of acquired customers who did not repeat within the measured cohort window",
  ],
  [
    "cod",
    "CoD means Cost of Delivery: outbound shipping, fulfillment, packaging, and return shipping divided by net revenue",
  ],
  [
    "cogs",
    glossaryDetail(
      "COGS means Cost of Goods Sold. The dashboard shows the share of net revenue spent on product costs",
      "100% - Product Margin %"
    ),
  ],
  [
    "cvr",
    "CVR means conversion rate. Audit insights only use this when session data is available",
  ],
  ["pdp", "PDP means Product Detail Page"],
  [
    "acquisition_efficiency_trend",
    "Compares latest full-month nCAC with the best comparable recent month using order cohorts and paid-channel activity, not website sessions",
  ],
  [
    "product margin",
    glossaryDetail(
      "Product Margin is the share of net revenue remaining after product costs, before shipping paid, transaction fees, marketing, and operating expenses",
      "(net revenue - COGS) / net revenue x 100"
    ),
  ],
  [
    "net profit margin",
    glossaryDetail(
      "Net Profit Margin is the share of net revenue remaining after product costs, shipping paid, transaction fees, direct marketing, and operating expenses",
      "net profit / net revenue x 100"
    ),
  ],
  [
    "cost of delivery",
    glossaryDetail(
      "Cost of Delivery is the share of net revenue spent on shipping paid and transaction fees. These costs account for the difference between product margin and gross profit margin",
      "Product Margin % - Gross Profit Margin %"
    ),
  ],
  [
    "operating expenses",
    glossaryDetail(
      "Operating Expenses is the share of net revenue spent on operating costs, excluding product costs, shipping paid, transaction fees, and direct marketing",
      "operating expenses / net revenue x 100"
    ),
  ],
  [
    "gross profit margin",
    glossaryDetail(
      "Gross Profit Margin is gross profit per order divided by average order value. Gross profit subtracts COGS, shipping paid, and transaction fees",
      "gross profit per order / AOV"
    ),
  ],
  [
    "contribution margin",
    glossaryDetail(
      "Contribution Margin is profit contribution divided by average order value after direct marketing costs",
      "(gross profit per order - direct marketing per order) / AOV"
    ),
  ],
  [
    "profit contribution",
    glossaryDetail(
      "Profit Contribution is gross profit minus direct marketing costs",
      "(gross profit per order - direct marketing per order) x orders"
    ),
  ],
  [
    "net profit",
    glossaryDetail(
      "Net Profit is profit contribution after operating expenses",
      "(profit contribution per order - operating expense per order) x orders"
    ),
  ],
  [
    "new aov",
    glossaryDetail(
      "New AOV is average order value for newly acquired customers. It subtracts discounts and refunds, and includes shipping collected",
      "(new gross sales - new discounts - new refunds + new shipping collected) / new orders"
    ),
  ],
  [
    "amer (0-day ltr:ncac)",
    glossaryDetail(
      "aMER compares new-customer day-0 revenue with nCAC: new AOV divided by new customer acquisition cost",
      "new AOV / nCAC"
    ),
  ],
  [
    "0-day ltcm:ncac",
    glossaryDetail(
      "0-Day LTCM:nCAC compares first-order contribution margin with new customer acquisition cost",
      "aMER x (new gross profit per order / new AOV)"
    ),
  ],
  [
    "ltcm:ncac",
    glossaryDetail(
      "LTCM:nCAC compares lifetime contribution margin with new customer acquisition cost",
      "lifetime contribution margin / nCAC"
    ),
  ],
  [
    "new customers",
    "New Customers is the count of orders from newly acquired customers in the selected period",
  ],
  [
    "new refund rate",
    glossaryDetail(
      "New Refund Rate is refunds divided by net sales after discounts for new-customer orders",
      "new refunds / (new gross sales - new discounts)"
    ),
  ],
  [
    "new discount rate",
    glossaryDetail(
      "New Discount Rate is discounts divided by gross sales for new-customer orders",
      "new discounts / new gross sales"
    ),
  ],
  [
    "repeat aov",
    glossaryDetail(
      "Repeat AOV is average order value for returning-customer orders. It subtracts discounts and refunds, and includes shipping collected",
      "(repeat gross sales - repeat discounts - repeat refunds + repeat shipping collected) / repeat orders"
    ),
  ],
  [
    "repeat discount rate",
    glossaryDetail(
      "Repeat Discount Rate is discounts divided by gross sales for returning-customer orders",
      "repeat discounts / repeat gross sales"
    ),
  ],
  [
    "repeat refund rate",
    glossaryDetail(
      "Repeat Refund Rate is refunds divided by net sales after discounts for returning-customer orders",
      "repeat refunds / (repeat gross sales - repeat discounts)"
    ),
  ],
  [
    "repeat customers",
    "Repeat Customers is the count of orders from returning customers in the selected period",
  ],
  [
    "30-day repeat rate",
    glossaryDetail(
      "30-Day Repeat Rate is the share of acquired customers who placed a repeat order within the measured 30-day cohort window",
      "repeat customers / acquired customers x 100"
    ),
  ],
  [
    "90-day ltr",
    glossaryDetail(
      "90-Day LTR is 90-day lifetime revenue per acquired customer",
      "average 90-day cohort revenue per customer"
    ),
  ],
  [
    "ncac payback period",
    glossaryDetail(
      "nCAC Payback Period is the number of months until cumulative contribution margin exceeds new customer acquisition cost",
      "first cohort month where cumulative contribution margin per customer > nCAC"
    ),
  ],
  [
    "free shipping threshold",
    "Free Shipping Threshold is the suggested order value needed to offer free shipping without dragging down order economics",
  ],
  [
    "highest repeat customer bucket",
    "Highest Repeat Customer Bucket is the AOV bucket with the strongest repeat-customer rate",
  ],
  [
    "breakeven order volume",
    "Breakeven Order Volume is the order count needed to cover fixed and variable costs",
  ],
  [
    "breakeven marketing spend",
    "Breakeven Marketing Spend is the marketing investment needed to reach breakeven order volume",
  ],
]);

const normalizeMetricGlossaryDefinition = (definition) => {
  if (!definition) return null;
  if (typeof definition === "string") {
    return {
      description: definition,
      formula: "",
    };
  }

  const description = String(definition.description || "").trim();
  if (!description) return null;

  return {
    description,
    formula: String(definition.formula || "").trim(),
  };
};

const resolveDashboardInsightMetricGlossaryDefinition = (factOrLabel) => {
  const key =
    typeof factOrLabel === "object"
      ? String(factOrLabel?.key || "")
          .toLowerCase()
          .trim()
      : String(factOrLabel || "")
          .toLowerCase()
          .trim();
  if (metricGlossaryDefinitions.has(key)) {
    return metricGlossaryDefinitions.get(key);
  }
  if (key.startsWith("ltc_cac_")) {
    return metricGlossaryDefinitions.get("ltcm:cac");
  }

  const label =
    typeof factOrLabel === "object"
      ? `${factOrLabel?.label || ""} ${factOrLabel?.key || ""}`
      : String(factOrLabel || "");
  const normalizedLabel = label.toLowerCase().replace(/\s+/g, " ").trim();

  if (normalizedLabel.includes("ltcm:cac")) {
    return metricGlossaryDefinitions.get("ltcm:cac");
  }
  if (metricGlossaryDefinitions.has(normalizedLabel)) {
    return metricGlossaryDefinitions.get(normalizedLabel);
  }
  if (normalizedLabel.includes("ltcm")) {
    return normalizedLabel.includes("ncac")
      ? metricGlossaryDefinitions.get("ltcm:ncac")
      : metricGlossaryDefinitions.get("ltcm");
  }
  if (normalizedLabel.includes("ltr")) {
    return metricGlossaryDefinitions.get("ltr");
  }
  if (normalizedLabel.includes("ncac")) {
    return metricGlossaryDefinitions.get("ncac");
  }
  if (normalizedLabel.includes("cac")) {
    return metricGlossaryDefinitions.get("cac");
  }
  if (normalizedLabel.includes("average order value")) {
    return metricGlossaryDefinitions.get("aov");
  }
  if (normalizedLabel.includes("units per transaction")) {
    return metricGlossaryDefinitions.get("upt");
  }
  if (normalizedLabel.includes("marketing efficiency ratio")) {
    return metricGlossaryDefinitions.get("mer");
  }
  if (normalizedLabel.includes("discount rate")) {
    return metricGlossaryDefinitions.get("dr%");
  }
  if (key === "gross_margin_pct" || normalizedLabel.includes("gross margin")) {
    return metricGlossaryDefinitions.get("gross profit margin");
  }
  if (normalizedLabel.includes("repeat purchase")) {
    return metricGlossaryDefinitions.get("repeat_purchase_pct");
  }
  if (normalizedLabel.includes("churn")) {
    return metricGlossaryDefinitions.get("churn");
  }

  return null;
};

const dashboardInsightSectionIconKeys = new Set([
  "info",
  "products",
  "comparison",
  "list",
  "chart",
  "table",
  "target",
  "sparkles",
]);

const multipleRatioMetricKeys = new Set([
  "mer",
  "ltc_cac_30",
  "ltc_cac_90",
  "ltc_cac_180",
]);

const metricSourceLabels = {
  aov: ["Sales & orders"],
  upt: ["Sales & orders"],
  order_count_30: ["Sales & orders"],
  order_count_90: ["Sales & orders"],
  total_sales_30: ["Sales & orders"],
  total_sales_90: ["Sales & orders"],
  gross_margin_pct: ["Sales & orders"],
  discount_rate_pct: ["Sales & orders"],
  churn_pct: ["Customers", "Sales & orders"],
  repeat_purchase_pct: ["Customers", "Sales & orders"],
  mer: ["Marketing", "Sales & orders"],
  cac: ["Marketing", "Customers"],
  ltc_cac_30: ["Marketing", "Customers", "Sales & orders"],
  ltc_cac_90: ["Marketing", "Customers", "Sales & orders"],
  ltc_cac_180: ["Marketing", "Customers", "Sales & orders"],
  acquisition_efficiency_trend: ["Marketing", "Customers", "Sales & orders"],
  sku_concentration: ["Product sales"],
  data_freshness: ["Sales & orders"],
};

const formatLabelWord = (word, index) => {
  const lowerWord = word.toLowerCase();
  if (acronymLabels.has(lowerWord)) return acronymLabels.get(lowerWord);
  return index === 0 ? titleCaseWord(lowerWord) : lowerWord;
};

export const formatDashboardInsightFactValue = (fact, currency) => {
  if (fact?.value === null || fact?.value === undefined) return null;

  const value = Number(fact.value);
  if (!Number.isFinite(value)) return String(fact.value);

  if (multipleRatioMetricKeys.has(String(fact.key || "").toLowerCase())) {
    return `${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    }).format(value)}x`;
  }

  if (fact.unit === "ratio") {
    return `${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    }).format(value * 100)}%`;
  }

  if (fact.unit === "currency")
    return createNumberFormat(fact.currency || currency).currency(value, 0);

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatDashboardInsightText = (value) => String(value || "").trim();

export const formatDashboardInsightLabel = (value) => {
  const override = metricLabelOverrides[String(value || "").toLowerCase()];
  if (override) return override;

  const text = formatDashboardInsightText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return "";

  return text.split(" ").map(formatLabelWord).join(" ");
};

export const getDashboardInsightMetricGlossaryDetails = (factOrLabel) =>
  normalizeMetricGlossaryDefinition(
    resolveDashboardInsightMetricGlossaryDefinition(factOrLabel)
  );

export const getDashboardInsightMetricGlossary = (factOrLabel) =>
  getDashboardInsightMetricGlossaryDetails(factOrLabel)?.description || "";

const uniqueStrings = (values = []) => {
  const seen = new Set();
  return values.filter((value) => {
    const text = String(value || "").trim();
    if (!text || seen.has(text)) return false;
    seen.add(text);
    return true;
  });
};

const clampDashboardInsightPriority = (value) => {
  const priority = Math.round(Number(value));
  if (!Number.isFinite(priority)) return DEFAULT_DASHBOARD_INSIGHT_PRIORITY;
  return Math.max(0, Math.min(100, priority));
};

const getTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getDashboardInsightPriorityMeta = (insight = {}) => {
  const score = clampDashboardInsightPriority(insight.priority);
  const band = score >= 80 ? "high" : score >= 50 ? "medium" : "low";
  const bandLabel =
    band === "high" ? "High" : band === "medium" ? "Medium" : "Low";

  return {
    band,
    bandLabel,
    score,
    label: `${bandLabel} ${score}`,
  };
};

export const getDashboardInsightChangeMeta = (insight = {}) => {
  if (insight.changeStatus === "NEW") {
    return {
      label: "New",
      description: "New from the latest audit",
      className:
        "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    };
  }

  if (insight.changeStatus === "CHANGED") {
    return {
      label: "Changed",
      description: "Underlying metrics changed since last seen",
      className:
        "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    };
  }

  return null;
};

export const compareDashboardInsightsByPriority = (left = {}, right = {}) => {
  const priorityDelta =
    getDashboardInsightPriorityMeta(right).score -
    getDashboardInsightPriorityMeta(left).score;
  if (priorityDelta !== 0) return priorityDelta;

  const lastSeenDelta =
    getTimestamp(right.lastSeenAt) - getTimestamp(left.lastSeenAt);
  if (lastSeenDelta !== 0) return lastSeenDelta;

  return getTimestamp(right.createdAt) - getTimestamp(left.createdAt);
};

const parseDateOnly = (value) => {
  if (!value || typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDateOnly = (value, options = {}) => {
  const date = parseDateOnly(value);
  if (!date) return value || "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
    ...options,
  }).format(date);
};

export const formatDashboardInsightGeneratedAt = (value, timezone = "UTC") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const options = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };

  try {
    return new Intl.DateTimeFormat("en-US", {
      ...options,
      timeZone: timezone || "UTC",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }
};

export const formatDashboardInsightRelativeTime = (value, now = Date.now()) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = date.getTime() - Number(now);
  const absMs = Math.abs(diffMs);
  if (absMs < 45 * 1000) return "just now";

  const units = [
    ["year", 365 * 24 * 60 * 60 * 1000],
    ["month", 30 * 24 * 60 * 60 * 1000],
    ["week", 7 * 24 * 60 * 60 * 1000],
    ["day", 24 * 60 * 60 * 1000],
    ["hour", 60 * 60 * 1000],
    ["minute", 60 * 1000],
  ];
  const [unit, unitMs] =
    units.find(([_unit, milliseconds]) => absMs >= milliseconds) ||
    units[units.length - 1];
  const valueInUnit = Math.round(diffMs / unitMs);

  return new Intl.RelativeTimeFormat("en-US", {
    numeric: "always",
  }).format(valueInUnit, unit);
};

export const formatDashboardInsightDateRange = (range = {}) => {
  if (!range.from || !range.to) return "";

  const from = formatDateOnly(range.from);
  const to = formatDateOnly(range.to);
  return `${from} - ${to}`;
};

const formatDashboardInsightWindowLabel = (range = {}) => {
  if (!range.from || !range.to) return "";

  return `${formatDateOnly(range.from)} to ${formatDateOnly(range.to)}`;
};

export const getDashboardInsightDataContext = (insight = {}) => {
  const payloadContext = insight.payload?.dataContext || {};
  const metricFacts = Array.isArray(insight.payload?.metricFacts)
    ? insight.payload.metricFacts
    : [];
  const fallbackSources = uniqueStrings(
    metricFacts.flatMap((fact) => metricSourceLabels[fact.key] || [])
  ).map((label) => ({ label }));
  const fallbackRanges = metricFacts
    .filter((fact) => fact.window?.from && fact.window?.to)
    .map((fact) => fact.window);

  return {
    generatedAt:
      payloadContext.generatedAt ||
      insight.payload?.generatedAt ||
      insight.generatedAt ||
      insight.lastSeenAt ||
      insight.createdAt ||
      null,
    timezone:
      payloadContext.timezone ||
      fallbackRanges[0]?.timezone ||
      metricFacts[0]?.window?.timezone ||
      "UTC",
    sources: Array.isArray(payloadContext.sources)
      ? payloadContext.sources
      : fallbackSources,
    dateRanges: Array.isArray(payloadContext.dateRanges)
      ? payloadContext.dateRanges
      : fallbackRanges,
    metricKeys: Array.isArray(payloadContext.metricKeys)
      ? payloadContext.metricKeys
      : metricFacts.map((fact) => fact.key).filter(Boolean),
  };
};

export const getDashboardInsightSourceSummary = (insight = {}) => {
  const { sources } = getDashboardInsightDataContext(insight);
  const labels = uniqueStrings(
    sources.map((source) => source?.label || source)
  );
  if (!labels.length) return "Store data";
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
};

export const getDashboardInsightRangeSummary = (insight = {}) => {
  const { dateRanges } = getDashboardInsightDataContext(insight);
  const ranges = uniqueStrings(
    dateRanges.map((range) =>
      range?.from && range?.to
        ? `${range.from}:${range.to}:${range.timezone || ""}`
        : ""
    )
  )
    .map((key) => {
      const [from, to, timezone] = key.split(":");
      return { from, to, timezone };
    })
    .filter((range) => range.from && range.to);

  if (!ranges.length) return "";
  if (ranges.length === 1) return formatDashboardInsightDateRange(ranges[0]);
  return `${ranges.length} date ranges`;
};

export const getDashboardInsightFacts = (insight = {}) =>
  Array.isArray(insight.payload?.metricFacts)
    ? insight.payload.metricFacts
        .filter((fact) => fact?.key && fact?.status !== "UNAVAILABLE")
        .map((fact) => {
          const label = formatDashboardInsightLabel(
            metricLabelOverrides[String(fact.key || "").toLowerCase()]
              ? fact.key
              : fact.label || fact.key
          );
          const value = formatDashboardInsightFactValue(
            fact,
            insight.payload?.dataContext?.currency || insight.currency
          );
          const windowLabel = formatDashboardInsightWindowLabel(fact.window);

          return value
            ? {
                key: fact.key,
                label,
                value,
                details: fact.details || null,
                comparison: fact.comparison || null,
                windowLabel,
                window: fact.window || null,
              }
            : null;
        })
        .filter(Boolean)
    : [];

const normalizeComparisonMetricKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const isMaterialComparisonCandidate = (candidate) => {
  if (!candidate || candidate.status !== "AVAILABLE") return false;
  if (candidate.material === true) return true;
  if (candidate.material === false) return false;

  const delta = Math.abs(Number(candidate.deltaPct));
  return (
    ["worse", "better"].includes(candidate.direction) &&
    Number.isFinite(delta) &&
    delta >= 0.1
  );
};

const getComparisonDirectionRank = (candidate) => {
  if (candidate?.direction === "worse") return 2;
  if (candidate?.direction === "better") return 1;
  return 0;
};

const isPrimaryMetricComparison = ({ fact, insight }) => {
  const insightMetricKey = normalizeComparisonMetricKey(insight?.metricKey);
  if (!insightMetricKey) return false;

  return [
    fact?.key,
    fact?.comparison?.metricKey,
    fact?.comparison?.selected?.metricKey,
  ].some((key) => normalizeComparisonMetricKey(key) === insightMetricKey);
};

export const getDashboardInsightPrimaryComparison = (insight = {}) => {
  const rankedComparisons = getDashboardInsightFacts(insight)
    .map((fact, index) => {
      const comparison = fact.comparison;
      const selected = comparison?.selected;

      return {
        fact,
        comparison,
        selected,
        index,
        primary: isPrimaryMetricComparison({ fact, insight }),
        directionRank: getComparisonDirectionRank(selected),
        deltaMagnitude: Math.abs(Number(selected?.deltaPct) || 0),
      };
    })
    .filter(
      ({ comparison, selected }) =>
        comparison?.type === "period_comparison" &&
        isMaterialComparisonCandidate(selected)
    )
    .sort((left, right) => {
      if (left.primary !== right.primary) {
        return Number(right.primary) - Number(left.primary);
      }
      if (left.directionRank !== right.directionRank) {
        return right.directionRank - left.directionRank;
      }
      if (left.deltaMagnitude !== right.deltaMagnitude) {
        return right.deltaMagnitude - left.deltaMagnitude;
      }
      return left.index - right.index;
    });

  const primaryComparison = rankedComparisons[0];
  if (!primaryComparison) return null;

  return {
    fact: primaryComparison.fact,
    metricKey:
      primaryComparison.comparison.metricKey || primaryComparison.fact.key,
    metricLabel:
      primaryComparison.comparison.metricLabel || primaryComparison.fact.label,
    comparison: primaryComparison.comparison,
    selected: primaryComparison.selected,
    candidates: Array.isArray(primaryComparison.comparison.candidates)
      ? primaryComparison.comparison.candidates
      : [],
  };
};

const compactSectionText = (value, maxLength = 400) => {
  const text = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3).trim()}...`;
};

const normalizeDashboardSectionId = (value, fallback) => {
  const id = compactSectionText(value, 64)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return id || fallback;
};

const normalizeDashboardSectionBlock = (block) => {
  if (!block || typeof block !== "object") return null;

  if (block.type === "text") {
    const text = compactSectionText(block.text, 500);
    return text ? { type: "text", text } : null;
  }

  if (block.type === "bullets") {
    const items = Array.isArray(block.items)
      ? block.items
          .map((item) => compactSectionText(item, 180))
          .filter(Boolean)
          .slice(0, 6)
      : [];

    return items.length ? { type: "bullets", items } : null;
  }

  if (block.type === "key_values") {
    const rows = Array.isArray(block.rows)
      ? block.rows
          .map((row) => {
            const label = compactSectionText(row?.label, 80);
            const value = compactSectionText(row?.value, 180);
            return label && value ? { label, value } : null;
          })
          .filter(Boolean)
          .slice(0, 10)
      : [];

    return rows.length ? { type: "key_values", rows } : null;
  }

  if (block.type === "table") {
    const columns = Array.isArray(block.columns)
      ? block.columns
          .map((column) => compactSectionText(column, 60))
          .filter(Boolean)
          .slice(0, 4)
      : [];
    const rows = Array.isArray(block.rows)
      ? block.rows
          .map((row) => {
            const cells = (Array.isArray(row) ? row : row?.cells || [])
              .map((cell) => compactSectionText(cell, 100))
              .slice(0, columns.length);
            return cells.length ? cells : null;
          })
          .filter(Boolean)
          .slice(0, 10)
      : [];

    return columns.length && rows.length
      ? { type: "table", columns, rows }
      : null;
  }

  return null;
};

const normalizeSectionComparisonText = (value) =>
  compactSectionText(value, 240)
    .toLowerCase()
    .replace(/\b(aud|cad|eur|gbp|nzd|usd)\b/g, "")
    .replace(/,/g, "")
    .replace(/[^a-z0-9.%:+-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const labelStopWords = new Set([
  "and",
  "best",
  "comparable",
  "current",
  "latest",
  "last",
  "month",
  "months",
  "proxy",
  "the",
  "vs",
  "yoy",
]);

const getComparableLabelTokens = (value) =>
  normalizeSectionComparisonText(value)
    .replace(/[:%+.-]/g, " ")
    .split(/\s+/)
    .filter((token) => token && !labelStopWords.has(token));

const labelsLikelyMatch = (sectionLabel, factLabel) => {
  const sectionText = normalizeSectionComparisonText(sectionLabel);
  const factText = normalizeSectionComparisonText(factLabel);
  if (!sectionText || !factText) return false;
  if (sectionText === factText) return true;
  if (factText.length >= 3 && sectionText.includes(factText)) return true;
  if (sectionText.length >= 3 && factText.includes(sectionText)) return true;

  const sectionTokens = new Set(getComparableLabelTokens(sectionText));
  const factTokens = getComparableLabelTokens(factText);
  if (!sectionTokens.size || !factTokens.length) return false;

  const sharedCount = factTokens.filter((token) =>
    sectionTokens.has(token)
  ).length;
  return sharedCount >= Math.min(2, factTokens.length);
};

const getComparableNumbers = (value) =>
  normalizeSectionComparisonText(value)
    .match(/-?\d+(?:\.\d+)?/g)
    ?.map((token) => Number(token))
    .filter(Number.isFinite) || [];

const numbersLikelyMatch = (left, right) => {
  const leftNumbers = getComparableNumbers(left);
  const rightNumbers = getComparableNumbers(right);

  return leftNumbers.some((leftNumber) =>
    rightNumbers.some((rightNumber) => {
      const tolerance = Math.max(
        0.01,
        Math.abs(leftNumber) >= 100 || Math.abs(rightNumber) >= 100 ? 0.5 : 0
      );
      return Math.abs(leftNumber - rightNumber) <= tolerance;
    })
  );
};

const valuesLikelyMatch = (sectionValue, factValue) => {
  const sectionText = normalizeSectionComparisonText(sectionValue);
  const factText = normalizeSectionComparisonText(factValue);
  if (!sectionText || !factText) return false;
  if (sectionText === factText) return true;
  if (sectionText.includes(factText) || factText.includes(sectionText)) {
    return true;
  }

  return numbersLikelyMatch(sectionText, factText);
};

const getSectionComparableRows = (section) =>
  section.blocks.flatMap((block) => {
    if (block.type === "key_values") {
      return block.rows.map((row) => ({
        label: row.label,
        value: row.value,
      }));
    }

    if (block.type === "table") {
      return block.rows
        .filter((row) => row.length >= 2)
        .map((row) => ({
          label: row[0],
          value: row.slice(1).join(" "),
        }));
    }

    return [];
  });

const sectionRowRepeatsMetricFact = (row, facts) =>
  facts.some(
    (fact) =>
      labelsLikelyMatch(row.label, fact.label) &&
      valuesLikelyMatch(row.value, fact.value)
  );

const isMetricFactRecapSection = (section, facts) => {
  if (!facts.length) return false;
  if (
    section.blocks.some(
      (block) => block.type !== "key_values" && block.type !== "table"
    )
  ) {
    return false;
  }

  const rows = getSectionComparableRows(section);
  if (rows.length < 2) return false;

  return rows.every((row) => sectionRowRepeatsMetricFact(row, facts));
};

export const getDashboardInsightPayloadSections = (insight = {}) =>
  Array.isArray(insight.payload?.sections)
    ? insight.payload.sections
        .map((section, index) => {
          if (!section || typeof section !== "object") return null;

          const blocks = Array.isArray(section.blocks)
            ? section.blocks
                .map(normalizeDashboardSectionBlock)
                .filter(Boolean)
                .slice(0, 4)
            : [];
          const title = compactSectionText(section.title, 80);
          const summary = compactSectionText(section.summary || title, 180);
          if (!title || !summary || !blocks.length) return null;

          return {
            id: normalizeDashboardSectionId(section.id, `section-${index + 1}`),
            title,
            summary,
            iconKey: dashboardInsightSectionIconKeys.has(section.iconKey)
              ? section.iconKey
              : "info",
            defaultOpen: false,
            blocks,
          };
        })
        .filter(Boolean)
        .filter(
          (section) =>
            !isMetricFactRecapSection(
              section,
              getDashboardInsightFacts(insight)
            )
        )
        .slice(0, 4)
    : [];

const truncateDashboardInsightTitle = (value) => {
  const text = formatDashboardInsightText(value);
  if (text.length <= DASHBOARD_INSIGHT_THREAD_TITLE_MAX) return text;
  return `${text.slice(0, DASHBOARD_INSIGHT_THREAD_TITLE_MAX - 3).trim()}...`;
};

export const buildDashboardInsightThreadTitle = (insight = {}) => {
  const title = formatDashboardInsightText(insight.title);
  return truncateDashboardInsightTitle(
    title ? `Action plan: ${title}` : "Insight action plan"
  );
};

export const buildDashboardInsightDisplayPayload = (insight = {}) => {
  const title = formatDashboardInsightText(insight.title);
  const summary = formatDashboardInsightText(insight.summary);
  const facts = getDashboardInsightFacts(insight);
  const sections = getDashboardInsightPayloadSections(insight);

  return {
    actionLabel: "Insight action plan",
    title: title || "Dashboard insight",
    summary,
    facts,
    sections,
    rangeSummary: getDashboardInsightRangeSummary(insight),
    sourceSummary: getDashboardInsightSourceSummary(insight),
    typeLabel: getDashboardInsightTypeMeta(insight.type).label,
  };
};

const compactDisplayPayloadText = (value, maxLength = 600) => {
  const text = formatDashboardInsightText(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3).trim()}...`;
};

const getJsonLength = (value) => {
  try {
    return JSON.stringify(value).length;
  } catch {
    return Infinity;
  }
};

const compactDashboardInsightDisplayFact = (fact) => ({
  key: fact.key || null,
  label: compactDisplayPayloadText(fact.label, 120),
  value: compactDisplayPayloadText(fact.value, 80),
  windowLabel: compactDisplayPayloadText(fact.windowLabel, 120),
});

const buildDashboardInsightActionContext = (insight = {}, displayPayload) => {
  const facts = displayPayload.facts
    .slice(0, DASHBOARD_INSIGHT_DISPLAY_FACT_LIMIT)
    .map(compactDashboardInsightDisplayFact);

  return {
    relevantComparison: buildDashboardInsightRelevantComparisonContext(insight),
    supportingSections: buildDashboardInsightSupportingSectionContext(insight),
    facts,
    sourceSummary: displayPayload.sourceSummary,
    rangeSummary: displayPayload.rangeSummary,
  };
};

const fitDashboardInsightDisplayPayload = (payload) => {
  if (getJsonLength(payload) <= DASHBOARD_INSIGHT_DISPLAY_PAYLOAD_MAX_LENGTH) {
    return payload;
  }

  const trimmed = {
    ...payload,
    summary: compactDisplayPayloadText(payload.summary, 500),
    facts: payload.facts.slice(0, 3),
  };
  if (getJsonLength(trimmed) <= DASHBOARD_INSIGHT_DISPLAY_PAYLOAD_MAX_LENGTH) {
    return trimmed;
  }

  return {
    actionLabel: payload.actionLabel,
    title: compactDisplayPayloadText(payload.title, 240),
    summary: compactDisplayPayloadText(payload.summary, 300),
    facts: payload.facts.slice(0, 3).map((fact) => ({
      key: fact.key,
      label: fact.label,
      value: fact.value,
    })),
    rangeSummary: compactDisplayPayloadText(payload.rangeSummary, 120),
    sourceSummary: compactDisplayPayloadText(payload.sourceSummary, 160),
    typeLabel: payload.typeLabel,
  };
};

const buildDashboardInsightActionDisplayPayload = (insight = {}) => {
  const displayPayload = buildDashboardInsightDisplayPayload(insight);
  const compactPayload = {
    actionLabel: displayPayload.actionLabel,
    title: compactDisplayPayloadText(displayPayload.title, 240),
    summary: compactDisplayPayloadText(displayPayload.summary, 1000),
    facts: displayPayload.facts
      .slice(0, DASHBOARD_INSIGHT_DISPLAY_FACT_LIMIT)
      .map(compactDashboardInsightDisplayFact),
    rangeSummary: compactDisplayPayloadText(displayPayload.rangeSummary, 120),
    sourceSummary: compactDisplayPayloadText(displayPayload.sourceSummary, 180),
    typeLabel: displayPayload.typeLabel,
    insightContext: buildDashboardInsightActionContext(insight, displayPayload),
  };

  return fitDashboardInsightDisplayPayload(compactPayload);
};

const formatCompactNumber = (value, options = {}) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    ...options,
  }).format(number);
};

const formatSignedPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
    style: "percent",
  }).format(number);
};

const getComparisonPeriodLabel = (row) =>
  row?.period || row?.month || row?.week || "";

const formatComparisonValue = (candidate, row) =>
  formatCompactNumber(row?.[candidate?.metricKey]);

const formatPeriodComparisonCandidate = (candidate) => {
  if (!candidate?.lens) return null;

  const label = candidate.label || candidate.lens;
  if (candidate.status !== "AVAILABLE") {
    return `${label} unavailable: ${candidate.reason || "not enough data"}`;
  }

  const currentPeriod = getComparisonPeriodLabel(candidate.current);
  const baselinePeriod = getComparisonPeriodLabel(candidate.baseline);
  const currentValue = formatComparisonValue(candidate, candidate.current);
  const baselineValue = formatComparisonValue(candidate, candidate.baseline);
  const change = formatSignedPercent(candidate.deltaPct);
  const direction = candidate.direction ? `, ${candidate.direction}` : "";

  return `${label} ${candidate.metricLabel}: ${currentPeriod} ${currentValue} vs ${baselinePeriod} ${baselineValue}, ${change}${direction}`;
};

const formatPeriodComparisonDetailLines = (comparison) => {
  if (comparison?.type !== "period_comparison") return [];

  const selectedLine = formatPeriodComparisonCandidate(comparison.selected);
  const selectedLens = comparison.selected?.lens;
  const candidateLines = Array.isArray(comparison.candidates)
    ? comparison.candidates
        .filter((candidate) => candidate?.lens !== selectedLens)
        .map(formatPeriodComparisonCandidate)
        .filter(Boolean)
    : [];

  return [
    selectedLine ? `Selected comparison: ${selectedLine}` : null,
    candidateLines.length
      ? `Comparison candidates: ${candidateLines.join("; ")}`
      : null,
  ].filter(Boolean);
};

const formatDashboardInsightRelevantComparisonPromptLines = (
  primaryComparison
) => {
  if (!primaryComparison?.comparison) return [];

  const selectedLine = formatPeriodComparisonCandidate(
    primaryComparison.selected
  );
  const selectedLens = primaryComparison.selected?.lens;
  const candidateLines = primaryComparison.candidates
    .filter((candidate) => candidate?.lens !== selectedLens)
    .map(formatPeriodComparisonCandidate)
    .filter(Boolean);

  return [
    selectedLine ? `Selected comparison: ${selectedLine}` : null,
    candidateLines.length
      ? `Other candidates for this metric: ${candidateLines.join("; ")}`
      : null,
  ].filter(Boolean);
};

const buildDashboardInsightRelevantComparisonContext = (insight = {}) => {
  const primaryComparison = getDashboardInsightPrimaryComparison(insight);
  if (!primaryComparison?.selected) return null;

  const selected = formatPeriodComparisonCandidate(primaryComparison.selected);
  const selectedLens = primaryComparison.selected?.lens;
  const candidates = primaryComparison.candidates
    .filter((candidate) => candidate?.lens !== selectedLens)
    .map((candidate) => ({
      lens: candidate.label || candidate.lens,
      summary: compactDisplayPayloadText(
        formatPeriodComparisonCandidate(candidate),
        180
      ),
    }))
    .filter((candidate) => candidate.summary)
    .slice(0, 4);

  return {
    metricLabel: compactDisplayPayloadText(
      primaryComparison.metricLabel || primaryComparison.fact?.label,
      120
    ),
    selected: compactDisplayPayloadText(selected, 220),
    candidates,
  };
};

const buildDashboardInsightSupportingSectionContext = (insight = {}) =>
  getDashboardInsightPayloadSections(insight)
    .map((section) => {
      const lines = formatDashboardInsightSectionPromptLines(section)
        .map((line) => compactDisplayPayloadText(line, 180))
        .slice(0, 3);
      return lines.length
        ? {
            id: section.id,
            title: compactDisplayPayloadText(section.title, 100),
            summary: compactDisplayPayloadText(section.summary, 160),
            lines,
          }
        : null;
    })
    .filter(Boolean)
    .slice(0, 4);

const formatDashboardInsightFactPromptLines = (
  fact,
  { includeComparisons = true } = {}
) => {
  const lines = [
    fact.windowLabel
      ? `${fact.label}: ${fact.value} (${fact.windowLabel})`
      : `${fact.label}: ${fact.value}`,
  ];

  if (fact.details?.type === "top_products") {
    lines.push(
      `${fact.details.label || "Top products"}: ${fact.details.rows
        .slice(0, 10)
        .map((row) => {
          const share = formatSignedPercent(row.share).replace("+", "");
          const sales = formatCompactNumber(row.totalSales);
          const quantity = formatCompactNumber(row.quantity, {
            maximumFractionDigits: 0,
          });

          return `#${row.rank} ${row.productName} (${sales} sales, ${quantity} units, ${share})`;
        })
        .join("; ")}`
    );
  }

  if (includeComparisons) {
    lines.push(...formatPeriodComparisonDetailLines(fact.comparison));
  }

  if (fact.comparison?.type === "best_month_baseline") {
    const { current, baseline, deltas } = fact.comparison;
    lines.push(
      `Current month ${current?.month}: nCAC ${formatCompactNumber(
        current?.ncac
      )}, paid conversion cost ${formatCompactNumber(
        current?.costPerAdConversion
      )}, new-customer yield ${formatSignedPercent(
        current?.newCustomerConversionProxy
      ).replace("+", "")}`
    );
    lines.push(
      `Best comparable month ${baseline?.month}: nCAC ${formatCompactNumber(
        baseline?.ncac
      )}, paid conversion cost ${formatCompactNumber(
        baseline?.costPerAdConversion
      )}, new-customer yield ${formatSignedPercent(
        baseline?.newCustomerConversionProxy
      ).replace("+", "")}`
    );
    lines.push(
      `Change vs baseline: nCAC ${formatSignedPercent(
        deltas?.ncacPct
      )}, paid conversion cost ${formatSignedPercent(
        deltas?.costPerAdConversionPct
      )}, new-customer yield ${formatSignedPercent(
        deltas?.newCustomerConversionProxyPct
      )}`
    );
  }

  return lines;
};

const formatDashboardInsightSectionPromptLines = (section) =>
  section.blocks
    .flatMap((block) => {
      if (block.type === "text") return [`${section.title}: ${block.text}`];
      if (block.type === "bullets") {
        return [
          `${section.title}: ${block.items
            .map((item) => `- ${item}`)
            .join(" ")}`,
        ];
      }
      if (block.type === "key_values") {
        return [
          `${section.title}: ${block.rows
            .map((row) => `${row.label}: ${row.value}`)
            .join("; ")}`,
        ];
      }
      if (block.type === "table") {
        return [
          `${section.title}: ${block.rows
            .map((row) =>
              row
                .map((cell, index) =>
                  block.columns[index]
                    ? `${block.columns[index]} ${cell}`
                    : cell
                )
                .join(", ")
            )
            .join("; ")}`,
        ];
      }

      return [];
    })
    .filter(Boolean);

const fitDashboardInsightPrompt = ({ contextParts, instructions }) => {
  const fullPrompt = [...contextParts, instructions]
    .filter(Boolean)
    .join("\n\n");
  if (fullPrompt.length <= DASHBOARD_INSIGHT_PROMPT_MAX_LENGTH) {
    return fullPrompt;
  }

  const contextBudget = Math.max(
    800,
    DASHBOARD_INSIGHT_PROMPT_MAX_LENGTH - instructions.length - 4
  );
  const compactContext = compactDisplayPayloadText(
    contextParts.filter(Boolean).join("\n\n"),
    contextBudget
  );

  return [compactContext, instructions].filter(Boolean).join("\n\n");
};

export const buildDashboardInsightPrompt = (insight = {}) => {
  const explicitPrompt = formatDashboardInsightText(insight.prompt);
  const title = formatDashboardInsightText(insight.title);
  const summary = formatDashboardInsightText(insight.summary);
  const primaryComparison = getDashboardInsightPrimaryComparison(insight);
  const relevantComparisonLines =
    formatDashboardInsightRelevantComparisonPromptLines(primaryComparison)
      .map((line) => compactDisplayPayloadText(line, 240))
      .slice(0, 2);
  const metricFacts = getDashboardInsightFacts(insight)
    .flatMap((fact) =>
      formatDashboardInsightFactPromptLines(fact, {
        includeComparisons: false,
      })
    )
    .map((line) => compactDisplayPayloadText(line, 220))
    .slice(0, DASHBOARD_INSIGHT_PROMPT_FACT_LINE_LIMIT);
  const insightSections = getDashboardInsightPayloadSections(insight)
    .flatMap(formatDashboardInsightSectionPromptLines)
    .map((line) => compactDisplayPayloadText(line, 220))
    .slice(0, DASHBOARD_INSIGHT_PROMPT_SECTION_LINE_LIMIT);

  if (
    !title &&
    !summary &&
    !explicitPrompt &&
    !metricFacts.length &&
    !relevantComparisonLines.length &&
    !insightSections.length
  ) {
    return "";
  }

  const instructions = [
    "Instructions:",
    "Use the insight text and audit facts above as the starting context.",
    "Explain what this means for the business in plain English.",
    "Identify the most likely drivers or places to inspect next using the store data available in this app.",
    "Give only the next actions, checks, and decision rules that materially apply to this insight. Do not pad the answer to a fixed count.",
    "You may query or calculate additional context, comparisons, or nearby date ranges if it makes the answer more useful. Keep the original audit facts clearly labeled if any added calculation uses a different date range, cohort, or formula.",
    "Use the store performance benchmarks where they help interpret severity.",
  ].join("\n");

  return fitDashboardInsightPrompt({
    contextParts: [
      "Help me turn this dashboard insight into a practical action plan.",
      title ? `Insight:\n${title}` : null,
      summary ? `Context:\n${compactDisplayPayloadText(summary, 600)}` : null,
      metricFacts.length
        ? `Audit facts:\n${metricFacts.map((fact) => `- ${fact}`).join("\n")}`
        : null,
      relevantComparisonLines.length
        ? `Relevant comparison:\n${relevantComparisonLines
            .map((line) => `- ${line}`)
            .join("\n")}`
        : null,
      insightSections.length
        ? `Expandable details:\n${insightSections
            .map((line) => `- ${line}`)
            .join("\n")}`
        : null,
      explicitPrompt
        ? `Suggested starting question:\n${compactDisplayPayloadText(
            explicitPrompt,
            300
          )}`
        : null,
    ],
    instructions,
  });
};

export const buildDashboardInsightActionRequest = (insight = {}) => {
  const prompt = buildDashboardInsightPrompt(insight);
  if (!prompt) return null;

  const displayPayload = buildDashboardInsightActionDisplayPayload(insight);
  const displayTitle = displayPayload.title || "dashboard insight";

  return {
    prompt,
    displayMessage: `Action plan request: ${displayTitle}`,
    displayKind: DASHBOARD_INSIGHT_ACTION_KIND,
    displayPayload,
    threadTitle: buildDashboardInsightThreadTitle(insight),
  };
};

export const getDashboardInsightTypeMeta = (type) =>
  INSIGHT_TYPE_META[type] || {
    label: "Insight",
    tone: "default",
  };

export const getDashboardInsightMetricTarget = (insight = {}) => {
  const metricKey = String(insight.metricKey || "").toLowerCase();
  return metricRouteTargets.get(metricKey) || null;
};
